import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Clock, Calendar, Check, X, Minus } from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getBlogPost } from "@/content/blogPosts";
import { SEO } from "@/components/seo/SEO";
import manifestLogo from "@/assets/manifest-logo.webp";
import MarketingNav from "@/components/MarketingNav";
import logoSortly from "@/assets/blog/logo-sortly.png";
import logoHomezada from "@/assets/blog/logo-homezada.png";
import logoNestegg from "@/assets/blog/logo-nestegg.png";
import logoItemtopia from "@/assets/blog/logo-itemtopia.png";
import logoSpreadsheet from "@/assets/blog/logo-spreadsheet.png";

const APP_LOGOS: Record<string, string> = {
  "Manifest": manifestLogo,
  "Sortly": logoSortly,
  "HomeZada": logoHomezada,
  "Nest Egg": logoNestegg,
  "Itemtopia": logoItemtopia,
  "Spreadsheet (Google Sheets / Excel)": logoSpreadsheet,
};

function getChildren(el: React.ReactElement): React.ReactNode {
  return (el.props as { children?: React.ReactNode }).children;
}

function extractText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (React.isValidElement(node)) return extractText(getChildren(node));
  return "";
}

function parseTableData(children: React.ReactNode): { headers: string[]; rows: string[][] } {
  const headers: string[] = [];
  const rows: string[][] = [];
  React.Children.forEach(children, (section) => {
    if (!React.isValidElement(section)) return;
    if (section.type === "thead") {
      React.Children.forEach(getChildren(section), (tr) => {
        if (!React.isValidElement(tr)) return;
        React.Children.forEach(getChildren(tr), (th) => {
          if (!React.isValidElement(th)) return;
          headers.push(extractText(getChildren(th)));
        });
      });
    }
    if (section.type === "tbody") {
      React.Children.forEach(getChildren(section), (tr) => {
        if (!React.isValidElement(tr)) return;
        const row: string[] = [];
        React.Children.forEach(getChildren(tr), (td) => {
          if (!React.isValidElement(td)) return;
          row.push(extractText(getChildren(td)));
        });
        if (row.length > 0) rows.push(row);
      });
    }
  });
  return { headers, rows };
}

function CellValue({ text }: { text: string }) {
  const t = text.trim();
  if (t === "Yes") {
    return (
      <span className="inline-flex items-center justify-center">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
        </span>
        <span className="sr-only">Yes</span>
      </span>
    );
  }
  if (/^Yes\b/.test(t)) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 shrink-0">
          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
        </span>
        <span className="text-[11px] text-[#6B7280] leading-tight">{t.slice(3).trim()}</span>
      </span>
    );
  }
  if (t === "No") {
    return (
      <span className="inline-flex items-center justify-center">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-50">
          <X className="h-3.5 w-3.5 text-red-300" strokeWidth={2.5} />
        </span>
        <span className="sr-only">No</span>
      </span>
    );
  }
  if (t === "Basic" || t === "Manual") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-50 shrink-0">
          <Minus className="h-3.5 w-3.5 text-amber-400" strokeWidth={2.5} />
        </span>
        <span className="text-[11px] text-[#9CA3AF]">{t}</span>
      </span>
    );
  }
  return <span>{text}</span>;
}

function ComparisonTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  const manifestIdx = headers.findIndex((h) => h.trim() === "Manifest");
  return (
    <div className="not-prose my-12 -mx-5 px-5">
      <div className="overflow-x-auto overscroll-x-contain [scrollbar-gutter:stable] [-webkit-overflow-scrolling:touch]">
        <div className="min-w-max rounded-xl border border-[#E5E5E0] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <table className="border-collapse text-[13px] min-w-[780px] w-max">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  className={`text-left font-semibold text-[11px] uppercase tracking-[0.05em] px-4 py-3.5 whitespace-nowrap border-b-2 align-middle ${
                    i === manifestIdx
                      ? "bg-[#0F766E] text-white border-b-[#0a5f58]"
                      : "bg-[#F3F3EE] text-[#666] border-b-[#E5E5E0]"
                  }`}
                >
                  {i === manifestIdx ? (
                    <span className="inline-flex items-center gap-2">
                      <img src={manifestLogo} alt="" className="h-5 w-5 rounded shrink-0" loading="lazy" decoding="async" />
                      <span className="inline-flex flex-col leading-tight">
                        <span>{h}</span>
                        <span className="text-[9px] font-medium tracking-[0.02em] normal-case text-white/70">Best overall</span>
                      </span>
                    </span>
                  ) : h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="group">
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-4 py-3 transition-colors ${
                      ri < rows.length - 1 ? "border-b border-[#F0F0EB]" : ""
                    } ${
                      ci === manifestIdx ? "bg-[#0F766E]/[0.04] group-hover:bg-[#0F766E]/[0.07]" : "bg-white group-hover:bg-[#FAFAF7]"
                    } ${
                      ci === 0 ? "font-medium text-[#222] whitespace-nowrap" : "text-[#555]"
                    }`}
                  >
                    {ci === 0 ? cell : <CellValue text={cell} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug ?? "");

  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `https://manifestme.me/blog/${post.slug}`;
  const ogImage = post.heroImage ?? "https://manifestme.me/og-image.png";
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.dateModified ?? post.date,
    image: ogImage,
    author: { "@type": "Organization", name: "Manifest", url: "https://manifestme.me" },
    publisher: {
      "@type": "Organization",
      name: "Manifest",
      logo: { "@type": "ImageObject", url: "https://manifestme.me/favicon.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };
  const breadcrumbListSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://manifestme.me" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://manifestme.me/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#111]">
      <SEO
        title={`${post.title} – Manifest Blog`}
        description={post.description}
        canonical={canonical}
        ogImage={ogImage}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.dateModified ?? post.date}
        jsonLd={[blogPostingSchema, breadcrumbListSchema]}
      />

      {/* Nav */}
      <MarketingNav />

      <article className="mx-auto max-w-[820px] px-5 pt-12 pb-20 animate-fade-in">
        {/* Back link */}
        <div className="mb-6">
          <Link to="/blog">
            <Button variant="ghost" size="sm" className="gap-1.5 text-[#6B7280] hover:text-[#111] -ml-2">
              <ArrowLeft className="h-4 w-4" />
              All articles
            </Button>
          </Link>
        </div>

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-10">
          <ol className="flex items-center flex-wrap gap-1.5 text-[12px] text-[#6B7280]">
            <li>
              <Link to="/" className="hover:text-[#111] transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-[#9CA3AF]">
              <ChevronRight className="h-3.5 w-3.5" />
            </li>
            <li>
              <Link to="/blog" className="hover:text-[#111] transition-colors">
                Blog
              </Link>
            </li>
            <li aria-hidden="true" className="text-[#9CA3AF]">
              <ChevronRight className="h-3.5 w-3.5" />
            </li>
            <li className="text-[#111] truncate max-w-[60ch]" aria-current="page">
              {post.title}
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-12">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[#0F766E] bg-[#0F766E]/8 px-2.5 py-1 rounded-full mb-4">
            {post.category}
          </span>
          <h1 className="font-editorial text-[clamp(1.75rem,4.5vw,2.75rem)] leading-[1.12] tracking-[-0.02em] mb-5">
            {post.title}
          </h1>
          <p className="text-[16px] leading-[1.6] text-[#6B7280] mb-6 max-w-[580px]">
            {post.description}
          </p>
          <div className="flex items-center gap-4 text-[12px] text-[#9CA3AF]">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </time>
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {post.readTime}
            </span>
          </div>
          <div className="mt-8 h-px bg-gradient-to-r from-[#E5E5E0] via-[#D4D4CF] to-[#E5E5E0]" />
        </header>

        {/* Content */}
        <div className="
          prose prose-neutral max-w-none

          prose-headings:font-editorial prose-headings:tracking-[-0.01em]

          prose-h1:text-[1.75rem] prose-h1:mt-12 prose-h1:mb-5 prose-h1:leading-[1.2]

          [&_h2]:font-editorial [&_h2]:text-[1.5rem] [&_h2]:mt-16 [&_h2]:mb-6 [&_h2]:leading-[1.2]
          [&_h2]:relative [&_h2]:pb-4 [&_h2]:tracking-[-0.015em]
          [&_h2::after]:content-[''] [&_h2::after]:absolute [&_h2::after]:bottom-0 [&_h2::after]:left-0
          [&_h2::after]:w-12 [&_h2::after]:h-[2.5px] [&_h2::after]:bg-[#0F766E]/40 [&_h2::after]:rounded-full [&_h2::after]:block

          [&_h3]:font-editorial [&_h3]:tracking-[-0.01em]

          prose-h3:text-[1.2rem] prose-h3:mt-10 prose-h3:mb-4 prose-h3:leading-[1.3] prose-h3:font-semibold
          [&_h3]:text-[#1F2937]

          prose-p:text-[15.5px] prose-p:leading-[1.8] prose-p:text-[#374151]
          [&_p]:mb-6

          prose-li:text-[15.5px] prose-li:leading-[1.8] prose-li:text-[#374151]
          [&_ul]:my-5 [&_ul]:space-y-1 [&_ul]:list-disc [&_ul]:pl-5
          [&_ol]:my-5 [&_ol]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5
          [&_li]:pl-1 [&_li::marker]:text-[#0F766E]/60

          prose-a:text-[#0F766E] prose-a:font-medium prose-a:no-underline
          prose-a:border-b prose-a:border-[#0F766E]/25
          hover:prose-a:border-[#0F766E]/60 prose-a:transition-colors

          prose-strong:text-[#111] prose-strong:font-semibold

          prose-table:text-[13.5px] prose-table:my-8 prose-table:w-full
          [&_table]:border [&_table]:border-[#E5E5E0] [&_table]:rounded-xl [&_table]:overflow-hidden
          [&_table]:shadow-[0_1px_3px_rgba(0,0,0,0.04)]
          [&_th]:text-left [&_th]:align-left [&_th]:font-semibold [&_th]:text-[#111] [&_th]:text-[12px]
          [&_th]:uppercase [&_th]:tracking-[0.04em]
          [&_th]:bg-[#F3F3EE] [&_th]:border-b [&_th]:border-[#E5E5E0]
          [&_th]:px-4 [&_th]:py-3 [&_th]:whitespace-nowrap
          [&_td]:border-b [&_td]:border-[#F3F3EE]/80 [&_td]:px-4 [&_td]:py-3
          [&_td]:text-[#374151] [&_td]:text-left
          [&_tr:last-child_td]:border-b-0
          [&_tbody_tr:hover]:bg-[#F9F9F6] [&_tbody_tr]:transition-colors

          prose-blockquote:border-l-[3px] prose-blockquote:border-l-[#0F766E]
          prose-blockquote:bg-[#0F766E]/[0.04] prose-blockquote:rounded-r-lg
          prose-blockquote:text-[#4B5563] prose-blockquote:not-italic
          prose-blockquote:py-4 prose-blockquote:px-5 prose-blockquote:my-8
          [&_blockquote_p]:text-[15px] [&_blockquote_p]:leading-[1.7]

          prose-hr:border-[#E5E5E0] prose-hr:my-14

          prose-img:rounded-xl prose-img:shadow-sm prose-img:my-8

          prose-code:text-[13.5px] prose-code:bg-[#F3F3EE] prose-code:px-1.5 prose-code:py-0.5
          prose-code:rounded prose-code:text-[#0F766E] prose-code:font-normal
          prose-code:before:content-[''] prose-code:after:content-['']

          [&_em]:text-[#6B7280]

          [&>p:first-of-type]:text-[17px] [&>p:first-of-type]:leading-[1.75]
          [&>p:first-of-type]:text-[#1F2937]
          [&>p:first-of-type:first-letter]:text-[3.2em] [&>p:first-of-type:first-letter]:font-editorial
          [&>p:first-of-type:first-letter]:float-left [&>p:first-of-type:first-letter]:leading-[0.85]
          [&>p:first-of-type:first-letter]:mr-2 [&>p:first-of-type:first-letter]:mt-1
          [&>p:first-of-type:first-letter]:text-[#0F766E]
        ">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h3: ({ children, ...props }) => {
                const text = extractText(children);
                const logo = APP_LOGOS[text.trim()];
                return (
                  <h3 {...props} className="flex items-center gap-3">
                    {logo && <img src={logo} alt="" className="h-8 w-8 rounded-lg object-contain shrink-0" loading="lazy" width={32} height={32} />}
                    {children}
                  </h3>
                );
              },
              table: ({ children, ...props }) => {
                const text = extractText(children);
                const isComparison = text.includes("Manifest") && text.includes("Sortly");
                if (isComparison) {
                  const data = parseTableData(children);
                  if (data.headers.length > 0 && data.rows.length > 0) {
                    return <ComparisonTable headers={data.headers} rows={data.rows} />;
                  }
                }
                return (
                  <div className="overflow-x-auto -mx-1 px-1 my-8">
                    <table {...props}>{children}</table>
                  </div>
                );
              },
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl border border-[#E5E5E0] bg-white p-10 shadow-sm text-center">
          <h2 className="font-editorial text-xl mb-2">Start your free home inventory</h2>
          <p className="text-[14px] text-[#6B7280] mb-5 max-w-[400px] mx-auto leading-[1.6]">
            Snap a photo, forward a receipt. Manifest builds your inventory with AI. Free trial, no credit card required.
          </p>
          <Link to="/login" className="inline-block rounded-[10px] bg-[#111] px-7 py-3 text-sm font-semibold text-white hover:bg-[#222] transition-colors">
            Get started free
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[820px] px-5 py-8 flex items-center justify-between text-[12px] text-[#9CA3AF]">
          <span>© {new Date().getFullYear()} Manifest</span>
          <div className="flex gap-4">
            <Link to="/blog" className="hover:text-[#6B7280]">Blog</Link>
            <Link to="/terms" className="hover:text-[#6B7280]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#6B7280]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
